import classNames from 'classnames';
import { ReactNode, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';

import { isUserService } from '@/shared/lib';
import {
  Button,
  ButtonSizes,
  ButtonVariant,
  ConditionView,
  Icon,
  LinkAsButton,
  ServiceCard,
  SkeletonServiceCard,
  Typography,
} from '@/shared/ui';

import { Breakpoints, useMediaQuery } from '@/hooks/useMediaQuery';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { getPublicRouteUrl } from '@/helpers/route';

import AuthContext from '@/contexts/Auth/AuthContext';

import { ServiceType, UserServiceExternal, UserService } from '@/api/types';

import './ServicesHomePageList.scss';

function isAllUserService(arr: Array<UserService | UserServiceExternal>): arr is UserService[] {
  return arr.every(isUserService);
}

const DEFAULT_MAX_VISIBLE_SERVICES = 5;

interface BaseProps {
  header: string;
  maxVisibleServices?: 1 | 2 | 3 | 4 | 5;
  redirectLink: Nullable<string>;
}

interface ServiceProps extends BaseProps {
  servicesType: ServiceType.INTERNAL;
  services: UserService[];
}

interface ExternalServiceProps extends BaseProps {
  servicesType: ServiceType.EXTERNAL;
  services: UserServiceExternal[];
}

type Props = (ServiceProps | ExternalServiceProps) & {
  isLoading: boolean;
};

const renderSkeletons = (): ReactNode[] =>
  Array.from({ length: DEFAULT_MAX_VISIBLE_SERVICES }, (_, idx) => idx).map((item) => (
    <SkeletonServiceCard data-test-id="skeleton-service-card" className="services-list-section__item" key={item} />
  ));

export const ServicesHomePageList: React.FC<Props> = ({
  header,
  maxVisibleServices = DEFAULT_MAX_VISIBLE_SERVICES,
  services,
  redirectLink,
  servicesType,
  isLoading,
}) => {
  const navigate = useNavigate();
  const matchXS = useMediaQuery(Breakpoints.SM);
  const { isLoggedIn } = useContext(AuthContext);

  const renderServices = useCallback(() => {
    const servicesToView = services.slice(0, maxVisibleServices);

    if (!servicesToView.length) {
      return null;
    }

    if (isAllUserService(servicesToView) || servicesType === ServiceType.INTERNAL) {
      return (servicesToView as UserService[]).map(({ name, icon, code }) => (
        <ServiceCard
          key={name}
          title={name}
          icon={icon}
          buttonLink={
            <LinkAsButton size={ButtonSizes.LG} to={getPublicRouteUrl(ROUTES_BY_SERVICE_CODE[code], isLoggedIn)}>
              Замовити
            </LinkAsButton>
          }
          data-test-id="internal-service-card"
        />
      ));
    } else {
      return servicesToView
        .sort((a, b) => a.order - b.order)
        .map(({ name, link, icon }) => (
          <ServiceCard
            key={name}
            buttonLink={
              <LinkAsButton size={ButtonSizes.LG} to={link}>
                Перейти <Icon name="go_out" />
              </LinkAsButton>
            }
            title={name}
            icon={icon ?? ''}
            data-test-id="external-service-card"
          />
        ));
    }
  }, [isLoggedIn, maxVisibleServices, services, servicesType]);

  return (
    <section
      data-test-id="services-list-section"
      className={classNames('services-list-section', `services-list-section--${servicesType}`)}
    >
      <div className="container">
        <div className="services-list-section__wrapper-inner">
          <Typography as="h2" variant="H1" className="services-list-section__header">
            {header}
          </Typography>
          <div className="services-list-section__container">
            {isLoading && !services.length && renderSkeletons()}
            {services && !isLoading && renderServices()}
            <ConditionView if={!!redirectLink}>
              <div className="row">
                <Button
                  className="services-list-section__all-services-button"
                  variant={matchXS ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY}
                  onClick={(): void => {
                    redirectLink && navigate(redirectLink);
                  }}
                >
                  Всі послуги
                  <Icon name="arrow_forward" />
                </Button>
              </div>
            </ConditionView>
          </div>
        </div>
      </div>
    </section>
  );
};
